import api from '@/services/api';
import type {
  Policy,
  CreatePolicyRequest,
  UpdatePolicyRequest,
  PolicyEvaluationResult,
  NextStepsResponse,
  PolicyQueryParams,
} from '@/types/policy';

const BASE_PATH = '/policies';

/**
 * Policy management API service.
 *
 * Handles CRUD operations for scheduling policies,
 * policy evaluation, and next-steps retrieval for
 * patient journeys.
 */
export const policyService = {
  // ── Policy CRUD ────────────────────────────────────────

  /**
   * Lists all policies, optionally filtered by campaign, type, or active status.
   */
  async getPolicies(params?: PolicyQueryParams): Promise<Policy[]> {
    const { data } = await api.get<{ items: Policy[] }>(BASE_PATH, { params });
    return data.items;
  },

  /**
   * Fetches a single policy by ID.
   */
  async getPolicy(id: string): Promise<Policy> {
    const { data } = await api.get<Policy>(`${BASE_PATH}/${id}`);
    return data;
  },

  /**
   * Creates a new policy.
   */
  async createPolicy(payload: CreatePolicyRequest): Promise<Policy> {
    const { data } = await api.post<Policy>(BASE_PATH, payload);
    return data;
  },

  /**
   * Updates an existing policy.
   */
  async updatePolicy(id: string, payload: UpdatePolicyRequest): Promise<Policy> {
    const { data } = await api.put<Policy>(`${BASE_PATH}/${id}`, payload);
    return data;
  },

  /**
   * Deletes a policy by ID.
   */
  async deletePolicy(id: string): Promise<void> {
    await api.delete(`${BASE_PATH}/${id}`);
  },

  /**
   * Activates a policy.
   */
  async activatePolicy(id: string): Promise<Policy> {
    const { data } = await api.put<Policy>(`${BASE_PATH}/${id}/activate`);
    return data;
  },

  /**
   * Deactivates a policy.
   */
  async deactivatePolicy(id: string): Promise<Policy> {
    const { data } = await api.put<Policy>(`${BASE_PATH}/${id}/deactivate`);
    return data;
  },

  // ── Evaluation ─────────────────────────────────────────

  /**
   * Evaluates all applicable policies for a given journey step.
   */
  async evaluate(
    journeyId: string,
    stepId: string,
  ): Promise<PolicyEvaluationResult[]> {
    const { data } = await api.get<PolicyEvaluationResult[]>(
      `${BASE_PATH}/evaluate`,
      { params: { journeyId, stepId } },
    );
    return data;
  },

  /**
   * Retrieves available next steps and blocked steps for a patient journey.
   */
  async getNextSteps(journeyId: string): Promise<NextStepsResponse> {
    const { data } = await api.get<NextStepsResponse>(
      `${BASE_PATH}/next-steps`,
      { params: { journeyId } },
    );
    return data;
  },
};
